/**
 * Estado + validação do formulário de geração (ExportGranularSelector).
 *
 * Espelha as `refine` do GenerateLgpdReportDtoSchema (R15) para validar no
 * cliente ANTES de chamar o backend e habilitar/desabilitar o botão Gerar:
 *   - titular-export  → requesterPersonId obrigatório
 *   - cascade-chain   → operationId obrigatório
 *   - period=custom   → periodStart + periodEnd obrigatórios
 */

import { defaultGranularityFor } from './types';

import type {
  GenerateLgpdReportRequest,
  LgpdReportRequestType,
  LgpdReportType,
  ReportGranularity,
  ReportPeriod,
} from './types';

export interface ReportFormState {
  granularity: ReportGranularity;
  period: ReportPeriod;
  periodStart: string;
  periodEnd: string;
  requesterPersonId: string;
  operationId: string;
  requestType: LgpdReportRequestType | '';
}

export function initialFormState(reportType: LgpdReportType): ReportFormState {
  return {
    granularity: defaultGranularityFor(reportType),
    period: '30d',
    periodStart: '',
    periodEnd: '',
    requesterPersonId: '',
    operationId: '',
    requestType: '',
  };
}

/** Retorna a primeira pendência de validação, ou null se o form está pronto. */
export function validateForm(reportType: LgpdReportType, form: ReportFormState): string | null {
  if (reportType === 'titular-export' && form.requesterPersonId.trim() === '') {
    return 'Informe o ID do titular (requesterPersonId).';
  }
  if (reportType === 'cascade-chain' && form.operationId.trim() === '') {
    return 'Informe o operationId da cadeia.';
  }
  if (form.period === 'custom') {
    if (form.periodStart === '' || form.periodEnd === '') {
      return 'Período customizado exige data inicial e final.';
    }
    if (new Date(form.periodStart).getTime() > new Date(form.periodEnd).getTime()) {
      return 'A data inicial deve ser anterior à final.';
    }
  }
  return null;
}

/** Monta o body do contrato a partir do form, omitindo campos vazios. */
export function buildGenerateRequest(
  reportType: LgpdReportType,
  form: ReportFormState,
): GenerateLgpdReportRequest {
  const body: GenerateLgpdReportRequest = {
    reportType,
    granularity: form.granularity,
    period: form.period,
    format: 'json',
  };
  if (form.period === 'custom') {
    body.periodStart = new Date(form.periodStart).toISOString();
    body.periodEnd = new Date(form.periodEnd).toISOString();
  }
  if (form.requesterPersonId.trim() !== '') {
    body.requesterPersonId = form.requesterPersonId.trim();
  }
  if (form.operationId.trim() !== '') {
    body.operationId = form.operationId.trim();
  }
  if (form.requestType !== '') {
    body.requestType = form.requestType;
  }
  return body;
}
